import * as THREE from 'three'
import { BALL } from '../constants.js'
import { buildSeamMask } from './ballSeams.js'

// ============================================================================
//  Ball — piłka rozmiar 7, tekstura proceduralna v2.
//
//  Szwy: 4 prawdziwe okręgi na sferze (równik + południk + 2 boczne) liczone
//  per-piksel z analitycznym antyaliasingiem (ballSeams.js) → ostre,
//  geometrycznie poprawne linie zamiast rozmytych sinusoid w UV.
//
//  Pebbling: luminancja basketball.jpg MODULUJE bazowy pomarańcz per-piksel
//  (bez trybów mieszania canvas → pełna kontrola kontrastu, faktura zawsze
//  widoczna). Kafelki TILES_X×TILES_Y kwadratowe (okrągłe kropki, bez
//  rozciągania); ewentualne parzyste rzędy lustrzane — maskują brak pionowej
//  bezszwowości JPG.
//
//  Obie mapy (kolor + relief) budowane w JEDNYM przebiegu ImageData.
//  Wszystko generowane raz przy starcie — zero kosztu w runtime.
// ============================================================================

const BASE = import.meta.env.BASE_URL || '/'

const W = 2048                       // equirect 2:1
const H = 1024
const TILES_X = 2                    // kafelki pebblingu wokół równika…
const TILES_Y = 1                    // …i od bieguna do bieguna (1024×1024 → kropki okrągłe)
const BALL_ORANGE = [181, 74, 16]    // głęboki, nasycony pomarańcz (#b54a10)
const SEAM_RGB = [24, 16, 9]         // niemal czarny szew (ciepły odcień)
const SEAM_WIDTH = 0.05              // pełna szerokość szwu w rad (~6 mm na piłce)
const SEAM_AA = 0.004                // antyaliasing ~1.3 px przy W=2048
const PEBBLE_CONTRAST = 0.7          // amplituda modulacji koloru luminancją pebblingu

export class Ball {
  constructor() {
    const { map, bump } = makeBallTextures()

    const mat = new THREE.MeshStandardMaterial({
      map,
      bumpMap: bump,
      bumpScale: 0.4,
      roughness: 0.88,
      metalness: 0.0,
      envMapIntensity: 0.45,         // mniej „mydlanych" odbić → głębszy pomarańcz
    })
    const geo = new THREE.SphereGeometry(BALL.RADIUS, 64, 40)
    this.mesh = new THREE.Mesh(geo, mat)
    this.mesh.name = 'Ball'
    this.mesh.castShadow = true
    this.mesh.position.set(0, BALL.RADIUS, 7)
    // Startowa poza: lekki przechył → od pierwszej klatki widać klasyczny
    // wzór (krzyż szwów + boczne łuki), nie nudny „melon" z 3 kreskami.
    this.mesh.rotation.set(0.5, 0.9, 0.15)
  }

  setPosition(x, y, z) {
    this.mesh.position.set(x, y, z)
  }

  get position() {
    return this.mesh.position
  }

  spin(axis, angle) {
    this.mesh.rotateOnWorldAxis(axis, angle)
  }
}

// Buduje mapę koloru (pomarańcz modulowany pebblingiem + szwy) i mapę reliefu
// (luminancja pebblingu + rowki szwów). Maska szwów liczona raz, wspólna.
function makeBallTextures() {
  const mask = buildSeamMask(W, H, { width: SEAM_WIDTH, aa: SEAM_AA })

  const cCol = makeCanvas()
  const gc = cCol.getContext('2d')
  const cBump = makeCanvas()
  const gb = cBump.getContext('2d')

  compose(gc, gb, mask, null)

  const map = new THREE.CanvasTexture(cCol)
  map.colorSpace = THREE.SRGBColorSpace
  map.wrapS = THREE.RepeatWrapping               // bezszwowe owinięcie wokół równika
  map.wrapT = THREE.ClampToEdgeWrapping
  map.anisotropy = 8

  const bump = new THREE.CanvasTexture(cBump)
  bump.colorSpace = THREE.NoColorSpace           // dane wysokości, nie kolor
  bump.wrapS = THREE.RepeatWrapping
  bump.wrapT = THREE.ClampToEdgeWrapping
  bump.anisotropy = 8

  // Pebbling dochodzi po wczytaniu JPG — do tego czasu piłka ma kolor i szwy.
  const img = new Image()
  img.onload = () => {
    compose(gc, gb, mask, pebbleLuminance(img))
    map.needsUpdate = true
    bump.needsUpdate = true
  }
  img.src = `${BASE}textures/basketball.jpg`

  return { map, bump }
}

function makeCanvas() {
  const c = document.createElement('canvas')
  c.width = W
  c.height = H
  return c
}

// Kafelkuje JPG TILES_X×TILES_Y (parzyste rzędy lustrzane) i zwraca
// luminancję per-piksel.
function pebbleLuminance(img) {
  const c = makeCanvas()
  const ctx = c.getContext('2d')
  const tw = W / TILES_X
  const th = H / TILES_Y
  for (let row = 0; row < TILES_Y; row++) {
    for (let col = 0; col < TILES_X; col++) {
      ctx.save()
      if (row % 2 === 1) {
        ctx.translate(0, (row + 1) * th)
        ctx.scale(1, -1)
        ctx.drawImage(img, col * tw, 0, tw, th)
      } else {
        ctx.drawImage(img, col * tw, row * th, tw, th)
      }
      ctx.restore()
    }
  }
  const px = ctx.getImageData(0, 0, W, H).data
  const lum = new Uint8ClampedArray(W * H)
  let sum = 0
  for (let j = 0, i = 0; j < lum.length; j++, i += 4) {
    const l = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2]
    lum[j] = l
    sum += l
  }
  lum.mean = sum / lum.length
  return lum
}

// Jeden przebieg: kolor = pomarańcz × modulacja pebblingiem → wtopiony szew;
// relief = luminancja pebblingu → rowek szwu. Bez getImageData/trybów blend.
function compose(gc, gb, mask, lum) {
  const icol = gc.createImageData(W, H)
  const ibump = gb.createImageData(W, H)
  const col = icol.data
  const bmp = ibump.data
  const mean = lum ? lum.mean : 128

  for (let j = 0, i = 0; j < mask.length; j++, i += 4) {
    const m = mask[j]
    const f = lum ? 1 + (PEBBLE_CONTRAST * (lum[j] - mean)) / 255 : 1

    // kolor: baza × pebbling, wtopienie szwu wg maski (antyaliasing w masce)
    col[i] = BALL_ORANGE[0] * f + (SEAM_RGB[0] - BALL_ORANGE[0] * f) * m
    col[i + 1] = BALL_ORANGE[1] * f + (SEAM_RGB[1] - BALL_ORANGE[1] * f) * m
    col[i + 2] = BALL_ORANGE[2] * f + (SEAM_RGB[2] - BALL_ORANGE[2] * f) * m
    col[i + 3] = 255

    // relief: pebbling jako wysokość, szew jako głęboki rowek
    const h = lum ? lum[j] : 128
    const hb = h + (25 - h) * m
    bmp[i] = bmp[i + 1] = bmp[i + 2] = hb
    bmp[i + 3] = 255
  }

  gc.putImageData(icol, 0, 0)
  gb.putImageData(ibump, 0, 0)
}
